import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  CalendarDays,
  Plus,
  Sparkles,
  Download,
  MapPin,
  Users,
  ExternalLink,
  Loader2,
  Trash2,
  Edit,
  AlertTriangle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const localizer = momentLocalizer(moment);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EVENT_CATEGORIES = {
  sports: { color: '#ef4444', label: 'Sports', icon: '🏈' },
  music: { color: '#8b5cf6', label: 'Music & Concerts', icon: '🎵' },
  conference: { color: '#C9A46E', label: 'Conferences', icon: '🎤' },
  cultural: { color: '#C9A46E', label: 'Cultural', icon: '🎨' },
  holiday: { color: '#10b981', label: 'Holidays & Special', icon: '🎉' }
};

const BUSY_LEVELS = {
  low: { color: '#86efac', label: 'Low Demand' },
  normal: { color: '#D9B98F', label: 'Normal' },
  high: { color: '#fb923c', label: 'High Demand' },
  extreme: { color: '#ef4444', label: 'Extreme Demand' }
};

const AdminEventsCalendar = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showAISearch, setShowAISearch] = useState(false);
  const [aiSearching, setAiSearching] = useState(false);
  const [aiResults, setAiResults] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [busyDates, setBusyDates] = useState([]);
  const [isEditing, setIsEditing] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'cultural',
    venue: '',
    address: '',
    start_date: '',
    end_date: '',
    all_day: true,
    expected_attendance: '',
    ticket_url: '',
    busy_level: 'normal',
    notes: ''
  });

  const [aiSearchParams, setAiSearchParams] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    categories: []
  });

  useEffect(() => {
    fetchEvents();
    fetchBusyDates();
  }, [currentDate]);

  const fetchEvents = async () => {
    try {
      const startOfMonth = moment(currentDate).startOf('month').subtract(1, 'month').format('YYYY-MM-DD');
      const endOfMonth = moment(currentDate).endOf('month').add(1, 'month').format('YYYY-MM-DD');
      
      const response = await axios.get(`${API}/admin/events?start_date=${startOfMonth}&end_date=${endOfMonth}`);
      
      const formattedEvents = response.data.map(event => ({
        ...event,
        start: new Date(event.start_date),
        end: event.end_date ? new Date(event.end_date) : new Date(event.start_date),
        title: event.title,
        allDay: event.all_day
      }));
      
      setEvents(formattedEvents);
    } catch (error) {
      console.error('Failed to fetch events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const fetchBusyDates = async () => {
    try {
      const startOfMonth = moment(currentDate).startOf('month').format('YYYY-MM-DD');
      const endOfMonth = moment(currentDate).endOf('month').format('YYYY-MM-DD');
      
      const response = await axios.get(`${API}/admin/events/busy-dates?start_date=${startOfMonth}&end_date=${endOfMonth}`);
      setBusyDates(response.data);
    } catch (error) {
      console.error('Failed to fetch busy dates:', error);
    }
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setFormData({
      title: event.title,
      description: event.description || '',
      category: event.category,
      venue: event.venue || '',
      address: event.address || '',
      start_date: moment(event.start_date).format('YYYY-MM-DDTHH:mm'),
      end_date: event.end_date ? moment(event.end_date).format('YYYY-MM-DDTHH:mm') : '',
      all_day: event.all_day || false,
      expected_attendance: event.expected_attendance || '',
      ticket_url: event.ticket_url || '',
      busy_level: event.busy_level || 'normal',
      notes: event.notes || ''
    });
    setIsEditing(true);
    setShowEventModal(true);
  };

  const handleSelectSlot = ({ start }) => {
    setSelectedEvent(null);
    setIsEditing(false);
    setFormData({
      title: '',
      description: '',
      category: 'cultural',
      venue: '',
      address: '',
      start_date: moment(start).format('YYYY-MM-DDTHH:mm'),
      end_date: '',
      all_day: true,
      expected_attendance: '',
      ticket_url: '',
      busy_level: 'normal',
      notes: ''
    });
    setShowEventModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.start_date) {
      toast.error('Title and start date are required');
      return;
    }

    try {
      const payload = {
        ...formData,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
        expected_attendance: formData.expected_attendance ? parseInt(formData.expected_attendance) : null
      };

      if (isEditing && selectedEvent) {
        await axios.put(`${API}/admin/events/${selectedEvent.id}`, payload);
        toast.success('Event updated!');
      } else {
        await axios.post(`${API}/admin/events`, payload);
        toast.success('Event created!');
      }
      
      setShowEventModal(false);
      fetchEvents();
      fetchBusyDates();
    } catch (error) {
      toast.error('Failed to save event');
    }
  };

  const handleDelete = async () => {
    if (!selectedEvent || !window.confirm('Delete this event?')) return;
    
    try {
      await axios.delete(`${API}/admin/events/${selectedEvent.id}`);
      toast.success('Event deleted');
      setShowEventModal(false);
      fetchEvents();
      fetchBusyDates();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handleAISearch = async () => {
    setAiSearching(true);
    try {
      const response = await axios.post(`${API}/admin/events/ai-search`, aiSearchParams);
      setAiResults(response.data);
      toast.success(`Found ${response.data.events_found} events!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'AI search failed');
    } finally {
      setAiSearching(false);
    }
  };

  const handleAddAIEvents = async (eventsToAdd) => {
    try {
      const payload = eventsToAdd.map(e => ({
        ...e,
        start_date: new Date(e.start_date).toISOString(),
        end_date: e.end_date ? new Date(e.end_date).toISOString() : null
      }));
      
      const response = await axios.post(`${API}/admin/events/bulk-add`, payload);
      toast.success(response.data.message);
      setAiResults(null);
      setShowAISearch(false);
      fetchEvents();
      fetchBusyDates();
    } catch (error) {
      toast.error('Failed to add events');
    }
  };

  const handleExport = async () => {
    try {
      window.open(`${BACKEND_URL}/api/admin/events/export/ics`, '_blank');
      toast.success('Calendar export started');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const eventStyleGetter = (event) => {
    const category = EVENT_CATEGORIES[event.category] || EVENT_CATEGORIES.cultural;
    return {
      style: {
        backgroundColor: category.color,
        borderRadius: '4px',
        opacity: 0.9,
        color: 'white',
        border: '0',
        fontSize: '12px'
      }
    };
  };

  const dayPropGetter = (date) => {
    const dateStr = moment(date).format('YYYY-MM-DD');
    const busyDate = busyDates.find(b => b.date === dateStr);
    
    if (busyDate) {
      const level = BUSY_LEVELS[busyDate.busy_level];
      return {
        style: {
          backgroundColor: level?.color + '20'
        }
      };
    }
    return {};
  };

  const CustomToolbar = ({ onNavigate, label }) => (
    <div className="flex items-center justify-between mb-4 p-4 bg-white rounded-lg shadow-sm">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => onNavigate('PREV')}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <Button variant="outline" size="sm" onClick={() => onNavigate('TODAY')}>
          Today
        </Button>
        <Button variant="outline" size="sm" onClick={() => onNavigate('NEXT')}>
          <ChevronRight className="w-4 h-4" />
        </Button>
        <h2 className="text-xl font-bold ml-4">{label}</h2>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => setView('month')} className={view === 'month' ? 'bg-gray-100' : ''}>
          Month
        </Button>
        <Button variant="outline" size="sm" onClick={() => setView('week')} className={view === 'week' ? 'bg-gray-100' : ''}>
          Week
        </Button>
        <Button variant="outline" size="sm" onClick={() => setView('day')} className={view === 'day' ? 'bg-gray-100' : ''}>
          Day
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <Card>
          <CardContent className="py-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
            Loading calendar...
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <CalendarDays className="w-8 h-8" />
          Miami Events Calendar
        </h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowAISearch(true)}>
            <Sparkles className="w-4 h-4 mr-2" />
            AI Event Finder
          </Button>
          <Button variant="outline" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" />
            Export ICS
          </Button>
          <Button onClick={() => { setIsEditing(false); setSelectedEvent(null); setShowEventModal(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Add Event
          </Button>
        </div>
      </div>

      {/* Busy Period Alerts */}
      {busyDates.filter(b => b.busy_level === 'high' || b.busy_level === 'extreme').length > 0 && (
        <Card className="mb-4 border-orange-200 bg-orange-50">
          <CardContent className="py-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              <span className="font-medium text-orange-800">Busy Period Alert:</span>
              <span className="text-orange-700">
                {busyDates.filter(b => b.busy_level === 'high' || b.busy_level === 'extreme').length} high-demand dates this month
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Category Legend */}
      <Card className="mb-4">
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-sm font-medium text-gray-500">Categories:</span>
            {Object.entries(EVENT_CATEGORIES).map(([key, val]) => (
              <div key={key} className="flex items-center gap-1">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: val.color }} />
                <span className="text-sm">{val.icon} {val.label}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Calendar */}
      <Card>
        <CardContent className="p-4">
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 700 }}
            onSelectEvent={handleSelectEvent}
            onSelectSlot={handleSelectSlot}
            selectable
            view={view}
            onView={setView}
            date={currentDate}
            onNavigate={setCurrentDate}
            eventPropGetter={eventStyleGetter}
            dayPropGetter={dayPropGetter}
            components={{ toolbar: CustomToolbar }}
            popup
          />
        </CardContent>
      </Card>

      {/* Event Modal */}
      <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isEditing ? 'Edit Event' : 'Add New Event'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Event Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Miami Heat vs Lakers"
                  required
                />
              </div>

              <div>
                <Label>Category *</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(EVENT_CATEGORIES).map(([key, val]) => (
                      <SelectItem key={key} value={key}>
                        {val.icon} {val.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Busy Level</Label>
                <Select value={formData.busy_level} onValueChange={(v) => setFormData({ ...formData, busy_level: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(BUSY_LEVELS).map(([key, val]) => (
                      <SelectItem key={key} value={key}>{val.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Start Date & Time *</Label>
                <Input
                  type="datetime-local"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label>End Date & Time</Label>
                <Input
                  type="datetime-local"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
              </div>

              <div>
                <Label>Venue</Label>
                <Input
                  value={formData.venue}
                  onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                  placeholder="e.g., Kaseya Center"
                />
              </div>

              <div>
                <Label>Expected Attendance</Label>
                <Input
                  type="number"
                  value={formData.expected_attendance}
                  onChange={(e) => setFormData({ ...formData, expected_attendance: e.target.value })}
                  placeholder="e.g., 20000"
                />
              </div>

              <div className="col-span-2">
                <Label>Address</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Full address"
                />
              </div>

              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="col-span-2">
                <Label>Ticket URL</Label>
                <Input
                  value={formData.ticket_url}
                  onChange={(e) => setFormData({ ...formData, ticket_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <div>
                {isEditing && (
                  <Button type="button" variant="destructive" onClick={handleDelete}>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setShowEventModal(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {isEditing ? 'Update Event' : 'Add Event'}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* AI Search Modal */}
      <Dialog open={showAISearch} onOpenChange={setShowAISearch}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-gold-500" />
              AI Event Finder - Miami-Dade
            </DialogTitle>
          </DialogHeader>
          
          {!aiResults ? (
            <div className="space-y-4">
              <p className="text-gray-600">
                Search for events happening in Miami-Dade County. The AI will find sports games, concerts, festivals, conferences, and more.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Month</Label>
                  <Select 
                    value={aiSearchParams.month.toString()} 
                    onValueChange={(v) => setAiSearchParams({ ...aiSearchParams, month: parseInt(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'].map((m, i) => (
                        <SelectItem key={i} value={(i + 1).toString()}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Year</Label>
                  <Select 
                    value={aiSearchParams.year.toString()} 
                    onValueChange={(v) => setAiSearchParams({ ...aiSearchParams, year: parseInt(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2025">2025</SelectItem>
                      <SelectItem value="2026">2026</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button onClick={handleAISearch} disabled={aiSearching} className="w-full">
                {aiSearching ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Searching for events...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Find Events
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-bold">
                  Found {aiResults.events_found} events for {aiResults.month} {aiResults.year}
                </h3>
                <Button variant="outline" size="sm" onClick={() => setAiResults(null)}>
                  Search Again
                </Button>
              </div>

              <div className="max-h-96 overflow-y-auto space-y-2">
                {aiResults.events.map((event, idx) => (
                  <Card key={idx} className="p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span>{EVENT_CATEGORIES[event.category]?.icon || '📅'}</span>
                          <span className="font-medium">{event.title}</span>
                          <Badge variant="outline" style={{ backgroundColor: EVENT_CATEGORIES[event.category]?.color + '20' }}>
                            {EVENT_CATEGORIES[event.category]?.label}
                          </Badge>
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          <span className="flex items-center gap-1">
                            <CalendarDays className="w-3 h-3" />
                            {event.start_date}
                            {event.venue && (
                              <>
                                <MapPin className="w-3 h-3 ml-2" />
                                {event.venue}
                              </>
                            )}
                          </span>
                        </div>
                        {event.description && (
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{event.description}</p>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button onClick={() => handleAddAIEvents(aiResults.events)} className="flex-1">
                  <Plus className="w-4 h-4 mr-2" />
                  Add All {aiResults.events_found} Events
                </Button>
                <Button variant="outline" onClick={() => setShowAISearch(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEventsCalendar;
